import { useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { useDrag } from "react-use-gesture";
import { ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";
import { useRecoilValue } from "recoil";
import { t } from "i18next";
import { imageViewModeState } from "../../recoil/upgrade";
import { formatThreshold } from "../../utils/formatThreshold";

const C = {
  text: "#1d1d1f",
  sub: "#6e6e73",
  surface: "rgba(255, 255, 255, 0.92)",
  track: "#e5e5ea",
  under: "#a1a1a6",
  over: "#de3b30",
  hairline: "rgba(0, 0, 0, 0.08)",
  hover: "rgba(0, 0, 0, 0.035)",
  selected: "rgba(0, 0, 0, 0.06)",
};

export interface LabelScoreRow {
  index: number;
  label: string;
  score: number;
  threshold: number;
  margin: number;
  isOver: boolean;
}

interface LabelScorePanelProps {
  labels: string[];
  scoreList: number[];
  thresholdList: number[];
  visibleIndex: number | null;
  onChangeVisible: (index: number | null) => void;
  sortByMargin?: boolean;
}

const clampPct = (v: number) => Math.min(100, Math.max(0, v * 100));

export function buildRows(
  labels: string[],
  scoreList: number[],
  thresholdList: number[],
  sortByMargin = true,
): LabelScoreRow[] {
  const n = Math.min(labels.length, scoreList.length, thresholdList.length);

  const rows: LabelScoreRow[] = Array.from({ length: n }, (_, i) => {
    const score = scoreList[i];
    const threshold = thresholdList[i];
    return {
      index: i,
      label: labels[i],
      score,
      threshold,
      margin: score - threshold,
      isOver: score >= threshold,
    };
  });

  return sortByMargin ? [...rows].sort((a, b) => b.margin - a.margin) : rows;
}

function ScoreBar({ score, threshold, isOver }: { score: number; threshold: number; isOver: boolean }) {
  return (
    <Box sx={{ position: "relative", height: 5, borderRadius: "3px", backgroundColor: C.track }}>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          height: 5,
          borderRadius: "3px",
          width: `${clampPct(score)}%`,
          backgroundColor: isOver ? C.over : C.under,
          transition: "width 180ms ease",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -3,
          left: `${clampPct(threshold)}%`,
          width: "1.5px",
          height: 11,
          backgroundColor: C.text,
          transform: "translateX(-0.75px)",
        }}
      />
    </Box>
  );
}

export default function LabelScorePanel({
  labels,
  scoreList,
  thresholdList,
  visibleIndex,
  onChangeVisible,
  sortByMargin = true,
}: LabelScorePanelProps) {
  const viewMode = useRecoilValue(imageViewModeState);
  const [collapsed, setCollapsed] = useState(false);
  // ponytail: 기본 위치(좌하단)에서의 이동량만 들고 간다. 컨테이너 밖으로 못 나가게 막으려면 여기서 clamp
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const originRef = useRef({ x: 0, y: 0 });
  const draggedRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  // 하단 기준(bottom)으로 붙어 있으면 접을 때 줄어든 높이만큼 헤더가 아래로 내려간다.
  // 접는 순간의 top을 고정해 기준을 위로 옮기고, 펼 때 다시 하단 기준으로 되돌린다.
  // ponytail: 접힌 채로 창이 리사이즈되면 top이 낡는다. 거슬리면 ResizeObserver로 다시 재기
  const [pinnedTop, setPinnedTop] = useState<number | null>(null);

  const toggleCollapse = () => {
    if (collapsed) {
      setPinnedTop(null);
      setCollapsed(false);
      return;
    }
    setPinnedTop(cardRef.current?.offsetTop ?? null);
    setCollapsed(true);
  };

  const bind = useDrag(({ movement: [mx, my], first }) => {
    if (first) originRef.current = offset;
    if (Math.abs(mx) + Math.abs(my) > 3) draggedRef.current = true;
    setOffset({ x: originRef.current.x + mx, y: originRef.current.y + my });
  });
  const rows = useMemo(
    () => buildRows(labels, scoreList, thresholdList, sortByMargin),
    [labels, scoreList, thresholdList, sortByMargin],
  );
  // ponytail: scoremap 레이어 선택용 컨트롤이라 그 모드가 아니면 띄울 이유가 없다
  if (viewMode !== "scoremap" || rows.length === 0) return null;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "absolute",
        left: 12,
        top: pinnedTop ?? "auto",
        bottom: pinnedTop === null ? 12 : "auto",
        zIndex: 5,
        width: 260,
        maxHeight: "60%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "10px",
        border: `0.5px solid var(--dk-border, ${C.hairline})`,
        boxShadow: "0 8px 32px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.07)",
        backgroundColor: C.surface,
        backdropFilter: "blur(8px)",
        overflow: "hidden",
        transform: `translate(${offset.x}px, ${offset.y}px)`,
      }}
    >
      <ButtonBase
        {...bind()}
        aria-expanded={!collapsed}
        // 드래그로 끝난 제스처는 접기/펴기로 취급하지 않는다
        onClick={() => {
          if (draggedRef.current) {
            draggedRef.current = false;
            return;
          }
          toggleCollapse();
        }}
        sx={{
          cursor: "move",
          touchAction: "none",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          px: 1.25,
          py: 0.75,
          borderBottom: collapsed ? "none" : `0.5px solid ${C.hairline}`,
          "&:hover": { backgroundColor: C.hover },
        }}
      >
        <Typography sx={{ fontSize: 12, color: C.sub }}>{t("Label")}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: C.sub }}>{t("ScoreThreshold")}</Typography>
          {collapsed ? <ChevronUp size={14} color={C.sub} /> : <ChevronDown size={14} color={C.sub} />}
        </Box>
      </ButtonBase>

      <Box sx={{ display: collapsed ? "none" : "block", overflowY: "auto", px: 0.5, py: 0.5 }}>
        {rows.map((row) => {
          const isVisible = visibleIndex === row.index;
          const Icon = isVisible ? Eye : EyeOff;

          return (
            <ButtonBase
              key={row.index}
              aria-pressed={isVisible}
              aria-label={`${row.label} ${t("Scoremap")} ${isVisible ? t("Hide") : t("Show")}`}
              onClick={() => onChangeVisible(isVisible ? null : row.index)}
              sx={{
                display: "block",
                width: "100%",
                textAlign: "left",
                px: 1.25,
                py: 1.15,
                borderRadius: "8px",
                backgroundColor: isVisible ? C.selected : "transparent",
                transition: "background-color 140ms ease",
                "&:hover": { backgroundColor: isVisible ? C.selected : C.hover },
                "&.Mui-focusVisible": { outline: `2px solid ${C.text}`, outlineOffset: "-2px" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 0.75,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                  <Icon size={14} style={{ flexShrink: 0, color: isVisible ? C.text : C.sub, alignSelf: "center" }} />
                  <Typography
                    noWrap
                    sx={{ fontSize: 13, fontWeight: row.isOver ? 500 : 400, color: row.isOver ? C.text : C.sub }}
                  >
                    {row.label}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 13, color: row.isOver ? C.over : C.sub }}>
                    {row.score?.toFixed(3) ?? "Error"}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.sub }}>/ {formatThreshold(row.threshold)}</Typography>
                </Box>
              </Box>

              <ScoreBar score={row.score} threshold={row.threshold} isOver={row.isOver} />
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
